import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BriefFlowStage, BriefFlowVariant, FlowCustomer } from './BriefFlowStage'
import { HubStats, STAR_BIG, STAR_LOW, STAR_TOP, compact } from './hubBrand'
import styles from './IntelligenceHubScene.module.css'

/*
 * Intelligence Hub banner: org info, fleet data and system health orbit the AI planet and
 * feed it, then the Health check brief resolves on the right. Shrunk, it becomes a one-line
 * summary beside the Intelligence Hub star.
 */

export type SceneStats = HubStats

/* the orbit scene, or one of the data-in, briefs-out alternatives (a demo option) */
export type HubBannerStyle = 'orbit' | BriefFlowVariant

interface Props {
  customerID?: string
  /** the team's customers: Circuit lets each live brief pick which one it opens (demo) */
  customers?: FlowCustomer[]
  variant?: HubBannerStyle
  /* Totals across the team's customers, shown on each source's label */
  stats?: SceneStats
}

type ItemKey = 'org' | 'fleet' | 'health'

interface OrbitItem {
  key: ItemKey
  colour: string
  light: string
  dark: string
  size: number
  side: 'right' | 'above' | 'below'
  rx: number
  ry: number
  speed: number
  title: string
  body: string
  path: (id: string) => string
  sentence: (s: SceneStats) => React.ReactNode
}

interface Pulse {
  colour: string
  t: number
  a0: number
  rx: number
  ry: number
}

const CX = 420
const CY = 220
const NEED = 15
const PULSE_MS = 700 // one halo pulse
const PULSE_TOTAL = PULSE_MS * 3 // three full pulses before the brief appears
const SPARK = 'M0 -20 C2 -6 6 -2 20 0 C6 2 2 6 0 20 C-2 6 -6 2 -20 0 C-6 -2 -2 -6 0 -20Z'
const VOLUME = [8010, 8150, 7900, 8200, 8100, 7850, 8100, 8050, 8100, 8100, 8000, 8250, 8150, 7700, 7605]
const AVG_Y = 320 - 0.8035 * 52
const SIZE_KEY = 'intelligence_hub_scene_size'
/* a tight, tilted ring that hugs the planet, as in the Multiverse logo */
const RING_RX = 74
const RING_RY = 19
const RING_TILT = -24
const INTRO_MS = 1900 // planets appear, then the orbits draw in, before any data moves
const PROMPT = 'How can I help you?'
const LABEL_MS = 10000 // each label stays up this long after it appears; hover brings it back
const TYPE_DELAY = 1950 // wait for the prompt to settle into the brief
const TYPE_MS = 60 // per character

const ITEMS: OrbitItem[] = [
  {
    key: 'org',
    colour: '#8EC640',
    light: '#D4EDAA',
    dark: '#3E6B14',
    size: 22,
    side: 'right',
    rx: 370,
    ry: 180,
    speed: 0.0015,
    title: 'Org info',
    body: 'Company profile, values and cost estimates',
    path: id => `/intelligence-hub/${id}/intel`,
    sentence: s =>
      s.customers !== undefined && s.fleetValue !== undefined ? (
        <>
          Profiles for <strong>{compact(s.customers)}</strong> customers, worth an estimated{' '}
          <strong>${compact(Math.round(s.fleetValue))}</strong> a year.
        </>
      ) : null,
  },
  {
    key: 'fleet',
    colour: '#32C2AF',
    light: '#B5F0E6',
    dark: '#0E5C55',
    size: 22,
    side: 'above',
    rx: 270,
    ry: 130,
    speed: -0.0021,
    title: 'Fleet data',
    body: 'Devices, print volume and usage trends',
    path: id => `/intelligence-hub/${id}`,
    sentence: s =>
      s.devices !== undefined && s.printVolume !== undefined ? (
        <>
          <strong>{compact(s.devices)}</strong> devices printed <strong>{compact(s.printVolume)}</strong> pages last
          month.
        </>
      ) : null,
  },
  {
    key: 'health',
    colour: '#27AA27',
    light: '#A6E8A0',
    dark: '#0D4A12',
    size: 22,
    side: 'below',
    rx: 170,
    ry: 82,
    speed: 0.0029,
    title: 'System health',
    body: 'Feature use, risk and renewal signals',
    path: id => `/intelligence-hub/${id}/briefs/health-check`,
    sentence: s =>
      s.customers !== undefined && s.riskFlags !== undefined ? (
        <>
          <strong>{compact(s.riskFlags)}</strong> customers flagged high risk,{' '}
          <strong>{compact(Math.max(0, s.customers - s.riskFlags))}</strong> looking healthy.
        </>
      ) : null,
  },
]
const START_ANGLES = [3.6, 1.0, 5.6]

const ease = (u: number) => (u < 0.5 ? 2 * u * u : 1 - (-2 * u + 2) ** 2 / 2)

const GLYPHS: Record<ItemKey, string> = {
  org:
    'M248,208H232V96a8,8,0,0,0,0-16H184V48a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16V208H24a8,8,0,0,0,0,16H248a8,8,0,0,0,0-16ZM80,72H96a8,8,0,0,1,0,16H80a8,8,0,0,1,0-16Zm-8,48a8,8,0,0,1,8-8H96a8,8,0,0,1,0,16H80A8,8,0,0,1,72,120Zm64,88H88V160h48Zm8-80H128a8,8,0,0,1,0-16h16a8,8,0,0,1,0,16Zm0-40H128a8,8,0,0,1,0-16h16a8,8,0,0,1,0,16Zm72,120H184V96h32Z',
  fleet:
    'M240,96v80a8,8,0,0,1-8,8H200v32a8,8,0,0,1-8,8H64a8,8,0,0,1-8-8V184H24a8,8,0,0,1-8-8V96c0-13.23,11.36-24,25.33-24H56V40a8,8,0,0,1,8-8H192a8,8,0,0,1,8,8V72h14.67C228.64,72,240,82.77,240,96ZM72,72H184V48H72Zm112,88H72v48H184Zm16-44a12,12,0,1,0-12,12A12,12,0,0,0,200,116Z',
  health:
    'M240,102c0,70-103.79,126.66-108.21,129a8,8,0,0,1-7.58,0c-3.35-1.8-63.55-34.69-92.68-80.89A4,4,0,0,1,34.92,144H72a8,8,0,0,0,6.66-3.56l9.34-14,25.34,38a8,8,0,0,0,9.16,3.16,8.23,8.23,0,0,0,4.28-3.34L140.28,144H160a8,8,0,0,0,8-8.53,8.18,8.18,0,0,0-8.25-7.47H136a8,8,0,0,0-6.66,3.56l-9.34,14-25.34-38a8,8,0,0,0-9.17-3.16,8.25,8.25,0,0,0-4.27,3.34L67.72,128H23.53a4,4,0,0,1-3.83-2.81A76.93,76.93,0,0,1,16,102,62.07,62.07,0,0,1,78,40c20.65,0,38.73,8.88,50,23.89C139.27,48.88,157.35,40,178,40A62.07,62.07,0,0,1,240,102Z',
}

const Glyph: FunctionComponent<{ item: ItemKey }> = ({ item }) => (
  <svg x='-11' y='-11' width='22' height='22' viewBox='0 0 256 256' className={styles.glyph}>
    <path d={GLYPHS[item]} fill='currentColor' />
  </svg>
)

/* On the planet the logo is white, with each star's opacity following its weight in the brand mark */
const PlanetStar: FunctionComponent = () => (
  <svg x={CX - 20} y={CY - 20} width='40' height='40' viewBox='-1 -1 48 48' className={styles.planetStar}>
    <path d={STAR_BIG} fillOpacity='1' />
    <path d={STAR_TOP} fillOpacity='0.75' />
    <path d={STAR_LOW} fillOpacity='0.55' />
  </svg>
)

/* Full-colour AI stars for the shrunk banner: one in each colour of the AI gradient */
const BrandStar: FunctionComponent = () => (
  <svg viewBox='-1 -1 48 48' className={styles.brandStar} aria-hidden='true'>
    <path d={STAR_BIG} fill='#27AA27' />
    <path d={STAR_TOP} fill='#32C2AF' />
    <path d={STAR_LOW} fill='#AB87FF' />
  </svg>
)

export const IntelligenceHubScene: FunctionComponent<Props> = ({ customerID, customers, stats, variant = 'orbit' }) => {
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tipRefs = useRef<(HTMLDivElement | null)[]>([])
  const itemRefs = useRef<(SVGGElement | null)[]>([])
  const hoverRef = useRef<number>(-1)
  const pausedRef = useRef(false)
  const doneRef = useRef<() => void>(() => undefined)
  const [status, setStatus] = useState<'playing' | 'paused' | 'done'>('playing')
  const [run, setRun] = useState(0)
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(SIZE_KEY) !== 'compact'
    } catch {
      return true
    }
  })
  doneRef.current = () => setStatus(prev => (prev === 'playing' ? 'done' : prev))

  const go = (path?: string) => {
    if (path) navigate(path)
  }

  const setActive = (i: number, on: boolean) => {
    hoverRef.current = on ? i : -1
    tipRefs.current[i]?.classList.toggle(styles.tipActive, on)
  }

  useEffect(() => {
    const svg = svgRef.current
    if (!expanded || !svg) return undefined
    const pick = <T extends Element>(role: string) => svg.querySelector(`[data-role="${role}"]`) as T | null
    const pulsesG = pick<SVGGElement>('pulses')
    const trail = pick<SVGPathElement>('trail')
    const glow = pick<SVGCircleElement>('glow')
    const back = pick<SVGGElement>('back')
    const front = pick<SVGGElement>('front')
    if (!pulsesG || !trail || !glow || !back || !front) return undefined

    const reduce =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const canAnimate = typeof trail.getTotalLength === 'function' && typeof window.requestAnimationFrame === 'function'

    const angles = START_ANGLES.slice()
    const pos = ITEMS.map(() => ({ x: 0, y: 0 }))
    const place = (i: number) => {
      const g = itemRefs.current[i]
      if (!g) return
      const it = ITEMS[i]
      pos[i] = { x: CX + it.rx * Math.cos(angles[i]), y: CY + it.ry * Math.sin(angles[i]) }
      const sc = 1.22 + 0.18 * Math.sin(angles[i])
      g.setAttribute('transform', `translate(${pos[i].x.toFixed(1)} ${pos[i].y.toFixed(1)}) scale(${sc.toFixed(3)})`)
      const layer = Math.sin(angles[i]) < 0 ? back : front
      if (g.parentNode !== layer) layer.appendChild(g)
    }
    ITEMS.forEach((_, i) => place(i))

    /*
     * Each label keeps one fixed position beside its planet. It fades in once during the intro, stays
     * for LABEL_MS, then fades out; hovering or focusing the planet brings it back.
     */
    const shownUntil = ITEMS.map(() => 0)
    let labelClock = 0
    const layoutLabels = () => {
      const root = rootRef.current
      if (!root) return
      const sr = root.getBoundingClientRect()
      const vr = svg.getBoundingClientRect()
      if (!sr.width || !vr.width) return
      const k = vr.width / 1200
      const ox = vr.left - sr.left
      const oy = vr.top - sr.top
      ITEMS.forEach((it, i) => {
        const el = tipRefs.current[i]
        if (!el) return
        const w = el.offsetWidth
        const h = el.offsetHeight
        const px = ox + pos[i].x * k
        const py = oy + pos[i].y * k
        const gap = (it.size + 10) * k
        const l = it.side === 'right' ? px + gap : px - w / 2
        const t = it.side === 'right' ? py - h / 2 : it.side === 'above' ? py - gap - h : py + gap
        el.style.left = `${l.toFixed(1)}px`
        el.style.top = `${t.toFixed(1)}px`
        el.classList.toggle(styles.tipShow, labelClock < shownUntil[i] || hoverRef.current === i)
      })
    }
    const showLabel = (i: number) => {
      shownUntil[i] = labelClock + LABEL_MS
    }

    const typed = pick<SVGTSpanElement>('typed')
    const typeTo = (n: number) => {
      if (typed) typed.textContent = PROMPT.slice(0, n)
    }

    const finish = () => {
      svg.classList.add(styles.revealed, styles.formed)
      trail.setAttribute('opacity', '0')
      doneRef.current()
    }

    if (reduce || !canAnimate) {
      finish()
      typeTo(PROMPT.length)
      ITEMS.forEach((_, i) => showLabel(i))
      layoutLabels()
      const onResize = () => layoutLabels()
      // without animation the labels still come down after LABEL_MS; hover re-lays them out
      const hide = window.setTimeout(() => {
        labelClock = LABEL_MS
        layoutLabels()
      }, LABEL_MS)
      const onHover = () => window.requestAnimationFrame(() => layoutLabels())
      window.addEventListener('resize', onResize)
      svg.addEventListener('mouseover', onHover)
      svg.addEventListener('mouseout', onHover)
      return () => {
        window.clearTimeout(hide)
        window.removeEventListener('resize', onResize)
        svg.removeEventListener('mouseover', onHover)
        svg.removeEventListener('mouseout', onHover)
      }
    }

    const pulses: Pulse[] = []
    const introAt = [INTRO_MS + 100, INTRO_MS + 1300, INTRO_MS + 2500]
    let know = 0
    let phase: 'learn' | 'pulse' | 'deliver' | 'formed' = 'learn'
    let phaseT = 0
    let clock = 0
    let acc = 0
    let next = 0
    let introIdx = 0
    let popped = false
    let last = performance.now()
    let raf = 0

    const emit = (i: number) => {
      pulses.push({
        colour: ITEMS[i].colour,
        t: 0,
        a0: Math.atan2(pos[i].y - CY, pos[i].x - CX),
        rx: ITEMS[i].rx,
        ry: ITEMS[i].ry,
      })
    }

    // a soft halo around the planet that brightens as it learns and pulses once it's ready
    const setGlow = () => {
      const beat =
        phase === 'pulse' ? 0.5 - 0.5 * Math.cos((Math.min(phaseT, PULSE_TOTAL) / PULSE_MS) * Math.PI * 2) : 0
      glow.setAttribute('opacity', (0.05 + 0.2 * know + 0.25 * beat).toFixed(3))
      glow.setAttribute('r', (70 + 14 * beat).toFixed(1))
    }

    const deliverStep = (u: number) => {
      const e = ease(u)
      const len = trail.getTotalLength()
      trail.setAttribute('opacity', (u < 0.5 ? u * 1.4 : 1.4 * (1 - u)).toFixed(3))
      trail.setAttribute('stroke-dasharray', `${len * e} ${len}`)
      if (u > 0.55) svg.classList.add(styles.revealed)
    }

    // labels appear one after another, then stay
    const runIntro = () => {
      if (introIdx < introAt.length && clock >= introAt[introIdx]) {
        showLabel(introIdx)
        introIdx += 1
      }
    }

    const drawPulses = (dt: number) => {
      while (pulsesG.firstChild) pulsesG.removeChild(pulsesG.firstChild)
      for (let i = pulses.length - 1; i >= 0; i -= 1) {
        const p = pulses[i]
        p.t += dt
        const u = Math.min(1, p.t / 1300)
        const s = 1 - ease(u)
        const a = p.a0 + u * Math.PI * 1.4
        const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        c.setAttribute('cx', (CX + p.rx * s * Math.cos(a)).toFixed(1))
        c.setAttribute('cy', (CY + p.ry * s * Math.sin(a)).toFixed(1))
        c.setAttribute('r', (5 - 2 * u).toFixed(2))
        c.setAttribute('fill', p.colour)
        pulsesG.appendChild(c)
        if (u >= 1) {
          pulses.splice(i, 1)
          if (phase === 'learn') {
            know = Math.min(1, know + 1 / NEED)
            if (know >= 1) {
              phase = 'pulse'
              phaseT = 0
            }
          }
        }
      }
    }

    const frame = (now: number) => {
      const dt = Math.min(50, now - last)
      last = now
      raf = window.requestAnimationFrame(frame)
      if (pausedRef.current) return
      phaseT += dt
      acc += dt
      clock += dt

      ITEMS.forEach((it, i) => {
        if (hoverRef.current !== i) angles[i] += it.speed * dt * 0.06
        place(i)
      })
      labelClock = clock
      // planets swap layers as they pass behind the AI planet; drop the pop-in so moving them can't replay it
      if (!popped && clock > INTRO_MS) {
        popped = true
        svg.querySelectorAll(`.${styles.popIn}`).forEach(el => el.classList.remove(styles.popIn))
      }
      layoutLabels()
      runIntro()

      if (clock > INTRO_MS && acc > (phase === 'learn' ? 260 : 900)) {
        acc = 0
        emit(next % ITEMS.length)
        next += 1
      }

      if (phase === 'pulse' && phaseT >= PULSE_TOTAL) {
        phase = 'deliver'
        phaseT = 0
      }
      if (phase === 'deliver') {
        const u = Math.min(1, phaseT / 1500)
        deliverStep(u)
        if (u >= 1) {
          phase = 'formed'
          phaseT = 0
          finish()
        }
      }
      if (phase === 'formed' && phaseT > TYPE_DELAY) {
        typeTo(Math.floor((phaseT - TYPE_DELAY) / TYPE_MS))
      }
      setGlow()
      drawPulses(dt)
    }

    raf = window.requestAnimationFrame(frame)
    return () => window.cancelAnimationFrame(raf)
    // the loop only touches refs; it restarts on replay or when the banner is expanded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, expanded, variant])

  const pauseOrReplay = () => {
    if (status === 'playing') {
      pausedRef.current = true
      setStatus('paused')
      return
    }
    pausedRef.current = false
    tipRefs.current.forEach(t => t?.classList.remove(styles.tipShow))
    setStatus('playing')
    setRun(r => r + 1)
  }

  const toggleSize = () => {
    const nextExpanded = !expanded
    pausedRef.current = false
    setStatus('playing')
    setExpanded(nextExpanded)
    try {
      localStorage.setItem(SIZE_KEY, nextExpanded ? 'expanded' : 'compact')
    } catch {
      // storage unavailable; the choice just won't persist
    }
  }

  const link = (fn: (id: string) => string) => (customerID ? fn(customerID) : undefined)
  const onKey = (path?: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      go(path)
    }
  }
  const summarySentence =
    stats &&
    [stats.customers, stats.fleetValue, stats.devices, stats.printVolume, stats.riskFlags].every(
      v => v !== undefined
    ) ? (
      <>
        Profiles for <strong>{compact(stats.customers || 0)}</strong> customers, worth an estimated{' '}
        <strong>${compact(Math.round(stats.fleetValue || 0))}</strong> a year.{' '}
        <strong>{compact(stats.devices || 0)}</strong> devices printed{' '}
        <strong>{compact(stats.printVolume || 0)}</strong> pages last month, and{' '}
        <strong>{compact(stats.riskFlags || 0)}</strong> customers are flagged high risk.
      </>
    ) : null
  const overview = link(id => `/intelligence-hub/${id}`)

  return (
    <div
      ref={rootRef}
      // the shrunk summary keeps its banner's look (Circuit's light pad, Horizon's space sky)
      className={`${styles.scene} ${status === 'paused' ? styles.paused : ''} ${expanded ? '' : styles.compact}${
        variant === 'pipeline' ? ` ${styles.circuit}` : variant === 'horizon' ? ` ${styles.horizon}` : ''
      }`}
      data-testid='intelligence-hub-scene'
    >
      <div className={styles.backdrop} aria-hidden='true' />

      {expanded && variant !== 'orbit' ? (
        <div className={styles.stage}>
          <BriefFlowStage
            key={run}
            variant={variant}
            stats={stats}
            customerID={customerID}
            customers={customers}
            onOpen={path => go(path)}
            onDone={() => doneRef.current()}
          />
        </div>
      ) : expanded ? (
        <div className={styles.stage}>
          <svg
            key={run}
            ref={svgRef}
            viewBox='0 0 1200 440'
            role='img'
            aria-label='Org info, fleet data and system health orbit the AI, which learns and produces a Health check brief'
          >
            <defs>
              <path id='ihs-spark' d={SPARK} />
              {/* the AI gradient, with its purple held for longer (as on Mission Control) */}
              <linearGradient id='ihs-btng' x1='0' y1='0' x2='1' y2='1'>
                <stop offset='0' stopColor='#27aa27' />
                <stop offset='.37' stopColor='#32c2af' />
                <stop offset='.7' stopColor='#ab87ff' />
                <stop offset='1' stopColor='#ab87ff' />
              </linearGradient>
              <linearGradient id='ihs-trailg' x1='0' y1='0' x2='1' y2='1'>
                <stop offset='0' stopColor='#27aa27' />
                <stop offset='.3' stopColor='#4bd2c0' />
                <stop offset='.47' stopColor='#7cded1' />
                <stop offset='.7' stopColor='#ab87ff' />
                <stop offset='1' stopColor='#ab87ff' />
              </linearGradient>
              <linearGradient id='ihs-askg' x1='0' y1='0' x2='1' y2='0'>
                <stop offset='0' stopColor='#ab87ff' />
                <stop offset='.3' stopColor='#ab87ff' />
                <stop offset='.63' stopColor='#4bd2c0' />
                <stop offset='1' stopColor='#27aa27' />
              </linearGradient>
              {ITEMS.map(it => (
                <radialGradient key={it.key} id={`ihs-planet-${it.key}`} cx='.42' cy='.38' r='.62'>
                  <stop offset='0' stopColor={it.light} stopOpacity='.45' />
                  <stop offset='.6' stopColor={it.colour} stopOpacity='.28' />
                  <stop offset='1' stopColor={it.colour} stopOpacity='.08' />
                </radialGradient>
              ))}
              {ITEMS.map(it => (
                <radialGradient key={`halo-${it.key}`} id={`ihs-halo-${it.key}`}>
                  <stop offset='.55' stopColor={it.colour} stopOpacity='.18' />
                  <stop offset='1' stopColor={it.colour} stopOpacity='0' />
                </radialGradient>
              ))}
              <radialGradient id='ihs-glowg'>
                <stop offset='.55' stopColor='#4bd2c0' stopOpacity='.9' />
                <stop offset='1' stopColor='#4bd2c0' stopOpacity='0' />
              </radialGradient>
            </defs>

            <path
              data-role='trail'
              d='M420 220 C580 120 800 110 1003 220'
              fill='none'
              stroke='url(#ihs-trailg)'
              strokeWidth='2.5'
              strokeLinecap='round'
              opacity='0'
            />

            {/* orbits draw themselves in once the planets have appeared */}
            <mask id='ihs-orbit-mask' maskUnits='userSpaceOnUse' x='0' y='0' width='1200' height='440'>
              {ITEMS.map((it, i) => (
                <ellipse
                  key={it.key}
                  cx={CX}
                  cy={CY}
                  rx={it.rx}
                  ry={it.ry}
                  pathLength={1}
                  className={`${styles.orbitReveal} ${styles[`orbitReveal${i + 1}`]}`}
                />
              ))}
            </mask>
            <g className={styles.orbits} mask='url(#ihs-orbit-mask)'>
              {ITEMS.map(it => (
                <ellipse key={it.key} cx={CX} cy={CY} rx={it.rx} ry={it.ry} />
              ))}
            </g>

            <g data-role='pulses' className={styles.noPointer} />
            <g data-role='back' />

            {/* planet: links to the customer overview */}
            <g
              className={styles.hit}
              tabIndex={0}
              role='link'
              aria-label='Open customer overview'
              onClick={() => go(overview)}
              onKeyDown={onKey(overview)}
            >
              <circle data-role='glow' cx={CX} cy={CY} r='70' fill='url(#ihs-glowg)' opacity='0.05' />
              <g className={styles.popIn}>
                <g transform={`translate(${CX} ${CY}) rotate(${RING_TILT})`}>
                  <ellipse rx={RING_RX} ry={RING_RY} className={styles.ring} />
                </g>
                <foreignObject x='286' y='86' width='268' height='268' className={styles.noPointer}>
                  <div className={styles.planetFace} />
                </foreignObject>
                <circle cx={CX} cy={CY} r='58' className={styles.focusRing} />
                <g transform={`translate(${CX} ${CY}) rotate(${RING_TILT})`}>
                  <path d={`M${-RING_RX} 0 A${RING_RX} ${RING_RY} 0 0 0 ${RING_RX} 0`} className={styles.ring} />
                </g>
                <PlanetStar />
              </g>
            </g>

            <g data-role='front' />
            {ITEMS.map((it, i) => (
              <g
                key={it.key}
                ref={el => {
                  itemRefs.current[i] = el
                }}
                className={styles.hit}
                tabIndex={0}
                role='link'
                aria-label={`${it.title}: ${it.body}`}
                onMouseEnter={() => setActive(i, true)}
                onMouseLeave={() => setActive(i, false)}
                onFocus={() => setActive(i, true)}
                onBlur={() => setActive(i, false)}
                onClick={() => go(link(it.path))}
                onKeyDown={onKey(link(it.path))}
              >
                {/* each source is a small planet of its own */}
                <g className={`${styles.popIn} ${styles[`popIn${i + 1}`]}`}>
                  <circle r={it.size * 1.5} fill={`url(#ihs-halo-${it.key})`} className={styles.noPointer} />
                  <circle r={it.size} fill={`url(#ihs-planet-${it.key})`} className={styles.tile} />
                  <g transform={`scale(${(it.size / 26).toFixed(3)})`}>
                    <Glyph item={it.key} />
                  </g>
                </g>
              </g>
            ))}

            {/* brief: the Health check the AI writes (an illustration, not a link) */}
            <g className={styles.brief}>
              <rect className={styles.frame} x='846' y='40' width='314' height='360' rx='6' />
              <g className={`${styles.sk} ${styles.sk1}`}>
                <rect x='866' y='62' width='104' height='12' rx='3' className={styles.ink2} />
                <rect x='866' y='90' width='132' height='40' rx='6' className={styles.ink1} />
                <rect x='1008' y='90' width='132' height='40' rx='6' className={styles.ink1} />
                <rect x='878' y='102' width='44' height='8' rx='3' className={styles.ink3} />
                <rect x='878' y='115' width='68' height='6' rx='3' className={styles.ink2} />
                <rect x='1020' y='102' width='30' height='8' rx='3' className={styles.ink3} />
                <rect x='1020' y='115' width='44' height='6' rx='3' className={styles.ink2} />
              </g>
              <g className={`${styles.sk} ${styles.sk2}`}>
                <rect x='866' y='142' width='274' height='82' rx='6' className={styles.panel} />
                <circle cx='906' cy='183' r='22' className={styles.donutTrack} />
                <path d='M906 161 A22 22 0 1 1 887 194' className={styles.donutValue} />
                <rect x='946' y='160' width='80' height='10' rx='3' className={styles.ink2} />
                <rect x='946' y='178' width='120' height='6' rx='3' className={styles.ink1} />
                <rect x='946' y='194' width='52' height='10' rx='3' className={styles.ink2} />
                <rect x='1004' y='196' width='40' height='6' rx='3' className={styles.ink1} />
              </g>
              <g className={`${styles.sk} ${styles.sk3}`}>
                <rect x='866' y='236' width='274' height='94' rx='6' className={styles.panel} />
                <rect x='880' y='248' width='70' height='8' rx='3' className={styles.ink2} />
                <g className={styles.bars}>
                  {VOLUME.map((v, i) => (
                    <rect
                      key={i}
                      x={882 + i * 16.8}
                      y={320 - (v / 10000) * 52}
                      width='11'
                      height={(v / 10000) * 52}
                      rx='1.5'
                      className={i >= 13 ? styles.barRecent : styles.bar}
                    />
                  ))}
                </g>
                <line x1='878' y1={AVG_Y} x2={882 + 15 * 16.8 - 2} y2={AVG_Y} className={styles.avg} />
              </g>
              {/* assistant input, as in the product (illustrative only), spanning the brief's content width */}
              <g className={styles.askin} aria-hidden='true'>
                <rect x='866' y='344' width='274' height='36' rx='6' className={styles.askFrame} />
                <rect x='871' y='349' width='264' height='26' rx='4' className={styles.askField} />
                <text x='881' y='366' className={styles.typing}>
                  <tspan data-role='typed' />
                  <tspan className={styles.caret}>|</tspan>
                </text>
                <rect x='1088' y='352' width='43' height='20' rx='3' fill='url(#ihs-btng)' />
                <use href='#ihs-spark' transform='translate(1098 362) scale(.22)' className={styles.askSpark} />
                <text x='1105' y='366' className={styles.askText}>
                  Ask
                </text>
              </g>
            </g>
          </svg>
        </div>
      ) : (
        <div className={styles.summary}>
          <div className={styles.summaryInner}>
            <BrandStar />
            <div className={styles.summaryText}>
              <h3>Your customers at a glance</h3>
              <p>
                {summarySentence ||
                  'Intelligence Hub brings together org info, fleet data and system health for your customers.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {expanded &&
        variant === 'orbit' &&
        ITEMS.map((it, i) => {
          const sentence = stats ? it.sentence(stats) : null
          return (
            <div
              key={it.key}
              ref={el => {
                tipRefs.current[i] = el
              }}
              className={styles.tip}
              aria-hidden='true'
            >
              <b>{it.title}</b>
              <p>{sentence || `${it.body}.`}</p>
            </div>
          )
        })}

      <div className={styles.controls}>
        {expanded && (
          <button
            type='button'
            className={styles.control}
            onClick={pauseOrReplay}
            aria-label={status === 'playing' ? 'Pause animation' : 'Replay animation'}
            title={status === 'playing' ? 'Pause' : 'Replay'}
          >
            {status === 'playing' ? (
              <svg viewBox='0 0 16 16' aria-hidden='true'>
                <rect x='4' y='3.5' width='2.6' height='9' rx='.8' fill='currentColor' />
                <rect x='9.4' y='3.5' width='2.6' height='9' rx='.8' fill='currentColor' />
              </svg>
            ) : (
              <svg viewBox='0 0 16 16' aria-hidden='true'>
                <path
                  d='M2.75 8a5.25 5.25 0 1 0 1.6-3.78M2.75 2.5v3.25H6'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </button>
        )}
        <button
          type='button'
          className={styles.control}
          onClick={toggleSize}
          aria-label={expanded ? 'Shrink banner' : 'Expand banner'}
          title={expanded ? 'Shrink' : 'Expand'}
        >
          {expanded ? (
            <svg viewBox='0 0 16 16' aria-hidden='true'>
              <path
                d='M6.5 2.5v4h-4M9.5 13.5v-4h4M6.5 6.5 2 2M9.5 9.5 14 14'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          ) : (
            <svg viewBox='0 0 16 16' aria-hidden='true'>
              <path
                d='M2.5 6.5v-4h4M13.5 9.5v4h-4M2.5 2.5 7 7M13.5 13.5 9 9'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}
